import { useFieldArray, useFormContext } from 'react-hook-form'
import { Plus, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { FieldError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface Props {
    idx: number
    /** Tên field-array cha (mặc định 'items'). */
    fieldName?: string
    /** Tên field lưu mảng lô con (mặc định 'splits'). */
    splitFieldName?: string
}

interface SplitRow {
    new_lot_code?: string
    quantity?: number
}

/**
 * LotSplitField — khu vực tách lô hiển thị INLINE ngay trong dòng "lô mới"
 * (không popup). Mỗi lô con: mã (auto nếu trống) + khối lượng, có nút xoá và lỗi
 * validate hiện ngay dưới ô. Tối thiểu 2 lô con (không cho xoá xuống dưới 2). Chỉ
 * khi DUYỆT phiếu backend mới nở item cha thành các lô thật.
 */
export function LotSplitField({
    idx,
    fieldName = 'items',
    splitFieldName = 'splits',
}: Props) {
    const { control, register, watch } = useFormContext()
    const name = `${fieldName}.${idx}.${splitFieldName}`
    const { fields, append, remove } = useFieldArray({ control, name })

    const rows = (watch(name) as SplitRow[] | undefined) ?? []
    const total = rows.reduce((sum, r) => sum + (Number(r?.quantity) || 0), 0)
    const canRemove = fields.length > 2

    return (
        <div className="space-y-2 rounded-md border border-dashed border-primary/40 bg-primary/5 p-3">
            <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-primary">
                    {m.lot_items_split_title()}
                </span>
                <span className="text-xs tabular-nums text-muted-foreground">
                    {m.lot_items_split_summary({
                        n: fields.length,
                        qty: formatQty(total),
                    })}
                </span>
            </div>

            {fields.map((field, i) => (
                <div key={field.id} className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                        <Input
                            aria-label={m.lot_items_new_lot_code()}
                            placeholder={m.lot_items_split_code_placeholder()}
                            {...register(`${name}.${i}.new_lot_code`)}
                        />
                        <FieldError name={`${name}.${i}.new_lot_code`} />
                    </div>
                    <div className="w-28 shrink-0">
                        <Input
                            type="number"
                            step="0.001"
                            min={0}
                            inputMode="decimal"
                            aria-label={m.lot_items_quantity()}
                            placeholder={m.lot_items_split_qty_placeholder()}
                            {...register(`${name}.${i}.quantity`, {
                                valueAsNumber: true,
                            })}
                        />
                        <FieldError name={`${name}.${i}.quantity`} />
                    </div>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="mt-0.5 shrink-0"
                        aria-label={m.lot_items_split_remove()}
                        disabled={!canRemove}
                        onClick={() => remove(i)}
                    >
                        <Trash2 className="size-4 text-destructive" />
                    </Button>
                </div>
            ))}

            <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => append({ new_lot_code: '', quantity: 0 })}
            >
                <Plus className="mr-1 size-4" />
                {m.lot_items_split_add_child()}
            </Button>
        </div>
    )
}
